import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { userRequest } from '../../utils/requestMethods';
import { BackButton, Header } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import { MdArrowBack, MdCheckCircle, MdClose, MdVisibility, MdDownload } from 'react-icons/md';
import { toast } from 'react-toastify';
import ErrorHandler from '../../utils/errorHandler';
import { getEducationLevelLabel } from '../../utils/enrollmentUtils';

// Required fields that are system-defined
const requiredEnrolTemplateFields = [
  "child_first_name",
  "child_last_name",
  "gender",
  "class",
  "dob",
  "nationality",
  "religion",
  "parent1_first_name",
  "parent1_last_name",
  "parent1_relationship",
  "parent1_occupation",
  "parent1_contact",
  "parent1_email",
  "parent1_address_line",
  "parent1_address_line2",
  "parent1_city",
  "parent1_state",
  "parent2_first_name",
  "parent2_last_name",
  "parent2_relationship",
  "parent2_occupation",
  "parent2_contact",
  "parent2_email",
  "parent2_address_line",
  "parent2_address_line2",
  "parent2_city",
  "parent2_state",
  "previous_school",
  "previous_class",
  "medical_conditions",
  "emergency_contact_person",
  "special_needs",
  "consent",
];

const EnrollmentDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { school } = useStateContext();
  const [loading, setLoading] = useState(true);
  const [enrollment, setEnrollment] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showFileModal, setShowFileModal] = useState(false);
  const schoolId = school?._id;
  const schoolSlug = school?.slug;

  const getFieldValue = (fields, fieldName) => {
    const field = fields?.find(f => f.name === fieldName);
    return field?.value || 'N/A';
  };

  const getFieldConfig = (fieldName) => {
    if (!formTemplate?.sections) return null;
    
    for (const section of formTemplate.sections) {
      const field = section.fields.find(f => f.name === fieldName);
      if (field) return field;
    }
    return null;
  };

  const getClassName = (classId) => {
    const classObj = classes.find(c => c._id === classId);
    return classObj?.class_name || classId;
  };

  const isFileField = (fieldName) => {
    const fieldConfig = getFieldConfig(fieldName);
    return fieldConfig?.type === 'file';
  };

  const isUrl = (value) => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const getFileType = (url) => {
    try {
      // Extract extension from URL, handling query parameters
      const urlWithoutParams = url.split('?')[0];
      const extension = urlWithoutParams.split('.').pop()?.toLowerCase();
      
      // Common file extensions mapping
      const extensionMap = {
        // Images
        'jpg': 'jpg', 'jpeg': 'jpg', 'png': 'png', 'gif': 'gif', 
        'bmp': 'bmp', 'webp': 'webp', 'svg': 'svg', 'tiff': 'tiff', 'ico': 'ico',
        
        // Documents
        'pdf': 'pdf', 'doc': 'doc', 'docx': 'docx', 'txt': 'txt', 
        'rtf': 'rtf', 'odt': 'odt', 'pages': 'pages',
        
        // Spreadsheets
        'xls': 'xls', 'xlsx': 'xlsx', 'csv': 'csv', 'ods': 'ods', 'numbers': 'numbers',
        
        // Presentations
        'ppt': 'ppt', 'pptx': 'pptx', 'odp': 'odp', 'key': 'key',
        
        // Archives
        'zip': 'zip', 'rar': 'rar', '7z': '7z', 'tar': 'tar', 'gz': 'gz',
        
        // Audio
        'mp3': 'mp3', 'wav': 'wav', 'flac': 'flac', 'aac': 'aac', 'ogg': 'ogg',
        
        // Video
        'mp4': 'mp4', 'avi': 'avi', 'mov': 'mov', 'wmv': 'wmv', 'flv': 'flv', 'mkv': 'mkv',
        
        // Code
        'js': 'js', 'html': 'html', 'css': 'css', 'json': 'json', 'xml': 'xml',
        'py': 'py', 'java': 'java', 'cpp': 'cpp', 'c': 'c', 'php': 'php'
      };
      
      return extensionMap[extension] || extension || 'unknown';
    } catch (error) {
      return 'unknown';
    }
  };

  const canPreview = (url) => {
    const fileType = getFileType(url);
    const previewableTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    return previewableTypes.includes(fileType);
  };

  const isDocument = (url) => {
    const fileType = getFileType(url);
    const documentTypes = [
      'pdf', 'doc', 'docx', 'txt', 'rtf', 'odt', 'pages'
    ];
    return documentTypes.includes(fileType);
  };

  const isSpreadsheet = (url) => {
    const fileType = getFileType(url);
    const spreadsheetTypes = ['xls', 'xlsx', 'csv', 'ods', 'numbers'];
    return spreadsheetTypes.includes(fileType);
  };

  const isPresentation = (url) => {
    const fileType = getFileType(url);
    const presentationTypes = ['ppt', 'pptx', 'odp', 'key'];
    return presentationTypes.includes(fileType);
  };

  const getFileIcon = (url) => {
    const fileType = getFileType(url);
    
    if (canPreview(url)) {
      // Image icon
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      );
    } else if (isDocument(url)) {
      // Document icon
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      );
    } else if (isSpreadsheet(url)) {
      // Spreadsheet icon
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 1v2h2V5H5zm4 0v2h2V5H9zm4 0v2h2V5h-2zM5 9v2h2V9H5zm4 0v2h2V9H9zm4 0v2h2V9h-2zM5 13v2h2v-2H5zm4 0v2h2v-2H9zm4 0v2h2v-2h-2z" clipRule="evenodd" />
        </svg>
      );
    } else if (isPresentation(url)) {
      // Presentation icon
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v8a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 1v6h10V5H5z" clipRule="evenodd" />
        </svg>
      );
    } else {
      // Generic file icon
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }
  };

  const handleFilePreview = (fileUrl, fieldName) => {
    setSelectedFile({ url: fileUrl, name: fieldName });
    setShowFileModal(true);
  };

  const formatFieldValue = (fieldName, value) => {
    if (value === 'N/A' || !value) return 'N/A';
    
    // Handle date fields
    if (fieldName === 'dob') {
      try {
        return new Date(value).toLocaleDateString();
      } catch {
        return value;
      }
    }
    
    // Handle class field
    if (fieldName === 'class') {
      return getClassName(value);
    }
    
    // Handle consent field
    if (fieldName === 'consent') {
      return value === 'true' ? 'Yes' : 'No';
    }
    
    return value;
  };

  const groupFieldsBySection = () => {
    if (!enrollment?.fields) {
      return [];
    }

    // If form template is not available, create a fallback structure
    if (!formTemplate?.sections) {
      return [{
        title: "Enrollment Information",
        description: "All submitted information",
        fields: enrollment.fields.map(field => ({
          name: field.name,
          label: field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          value: field.value,
          isRequired: requiredEnrolTemplateFields.includes(field.name),
          isCustom: !requiredEnrolTemplateFields.includes(field.name)
        }))
      }];
    }

    return formTemplate.sections.map(section => ({
      ...section,
      fields: section.fields.map(field => {
        const fieldValue = getFieldValue(enrollment.fields, field.name);
        return {
          ...field,
          value: fieldValue,
          isRequired: requiredEnrolTemplateFields.includes(field.name),
          isCustom: !requiredEnrolTemplateFields.includes(field.name)
        };
      }).filter(field => field.value !== 'N/A' || field.isRequired) // Show required fields even if empty
    }));
  };

  const fetchEnrollmentDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch enrollment details
      const enrollmentResponse = await userRequest.get(`/schools/${schoolSlug}/enrol-submission/${id}`);
      setEnrollment(enrollmentResponse.data);
      
      // TODO: Uncomment when template fetching is needed
      // Fetch form template to get field configurations
      // const levelOfEducation = enrollmentResponse.data.level_of_education;
      // const templateResponse = await userRequest.get(`/schools/${schoolSlug}/${levelOfEducation}/enrol-form-template`);
      // setFormTemplate(templateResponse.data);
      
      // Fetch classes for class name resolution
      try {
        const classesResponse = await userRequest.get(`/classes/slug/${schoolSlug}`);
        setClasses(classesResponse.data.classes || classesResponse.data || []);
      } catch (classesError) {
        console.warn('Could not fetch classes:', classesError);
        setClasses([]);
      }
      
    } catch (error) {
      ErrorHandler.handleError(error, {
        showToast: true,
        logToConsole: true
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (schoolId) {
      fetchEnrollmentDetails();
    }
  }, [schoolId]);

  const handleApprove = async () => {
    try {
      setLoading(true);
      await userRequest.patch(`/schools/${schoolId}/enrol-submissions/${id}`, {
        status: 'approved'
      });
      toast.success('Enrollment submission approved successfully');
      fetchEnrollmentDetails();
    } catch (error) {
      ErrorHandler.handleError(error, {
        showToast: true,
        logToConsole: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      await userRequest.patch(`/schools/${schoolId}/enrol-submissions/${id}`, {
        status: 'rejected'
      });
      toast.success('Enrollment submission rejected successfully');
      fetchEnrollmentDetails();
    } catch (error) {
      ErrorHandler.handleError(error, {
        showToast: true,
        logToConsole: true
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Enrollment details not found</p>
      </div>
    );
  }

  const status = enrollment?.status || 'pending';
  const statusText = status.charAt(0).toUpperCase() + status.slice(1);
  const statusStyles = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  };

  const renderFieldValue = (field) => {
    const formattedValue = formatFieldValue(field.name, field.value);
    
    // Check if this is a file field by:
    // 1. Field name patterns (common file field names)
    // 2. URL validation (must be a valid URL)
    // 3. File extension detection (must have a recognizable file extension)
    const hasFileFieldName = field.name.includes('certificate') || 
                            field.name.includes('result') || 
                            field.name.includes('document') ||
                            field.name.includes('photo') ||
                            field.name.includes('image') ||
                            field.name.includes('file') ||
                            field.name.includes('attachment') ||
                            field.name.includes('upload');
    
    const isValidUrl = isUrl(field.value);
    const hasFileExtension = isValidUrl && getFileType(field.value) !== 'unknown';
    
    // Consider it a file field if:
    // - It has a file field name pattern, OR
    // - It's a valid URL with a recognizable file extension
    const isFileField = hasFileFieldName || (isValidUrl && hasFileExtension);
    
    if (isFileField && isValidUrl) {
      const fileName = field.value.split('/').pop();
      const fileType = getFileType(field.value);
      const isPreviewable = canPreview(field.value);
      const isDoc = isDocument(field.value);
      
      return (
        <div className="flex items-start space-x-3">
          {/* File Icon and Name */}
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className="flex-shrink-0 text-blue-600">
              {getFileIcon(field.value)}
            </div>
            <div className="min-w-0 flex-1">
              <span className="font-medium text-gray-900 truncate block" title={fileName}>
                {fileName}
              </span>
              <span className="text-xs text-gray-500 uppercase">
                {fileType} file
              </span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {isPreviewable ? (
              <button
                onClick={() => handleFilePreview(field.value, field.label)}
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors border border-blue-200"
              >
                <MdVisibility className="mr-1" size={14} />
                Preview
              </button>
            ) : isDoc ? (
              <a
                href={field.value}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors border border-purple-200"
              >
                <svg className="mr-1 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                View Document
              </a>
            ) : isSpreadsheet(field.value) ? (
              <a
                href={field.value}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors border border-green-200"
              >
                <svg className="mr-1 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 1v2h2V5H5zm4 0v2h2V5H9zm4 0v2h2V5h-2zM5 9v2h2V9H5zm4 0v2h2V9H9zm4 0v2h2V9h-2zM5 13v2h2v-2H5zm4 0v2h2v-2H9zm4 0v2h2v-2h-2z" clipRule="evenodd" />
                </svg>
                View Spreadsheet
              </a>
            ) : isPresentation(field.value) ? (
              <a
                href={field.value}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors border border-orange-200"
              >
                <svg className="mr-1 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v8a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 1v6h10V5H5z" clipRule="evenodd" />
                </svg>
                View Presentation
              </a>
            ) : (
              <a
                href={field.value}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors border border-gray-200"
              >
                <MdVisibility className="mr-1" size={14} />
                View File
              </a>
            )}
            
            <a
              href={field.value}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors border border-green-200"
            >
              <MdDownload className="mr-1" size={14} />
              Download
            </a>
          </div>
        </div>
      );
    }
    
    return <span className="font-medium text-gray-900">{formattedValue}</span>;
  };

  const renderField = (field) => (
    <div key={field.name} className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center space-x-2">
          <p className="text-sm text-gray-500">{field.label}</p>
          {field.isRequired && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Required
            </span>
          )}
          {field.isCustom && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Custom
            </span>
          )}
        </div>
        <div className="mt-1">
          {renderFieldValue(field)}
        </div>
      </div>
    </div>
  );

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center">
          <BackButton history={history} />
        </div>
        {status === 'pending' && (
          <div className="flex gap-4 float-right">
            <button
              onClick={handleApprove}
              className="flex items-center px-4 py-2 bg-green-500 text-white rounded-full hover:bg-green-600"
            >
              <MdCheckCircle className="mr-2" />
              Approve
            </button>
            <button
              onClick={handleReject}
              className="flex items-center px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600"
            >
              <MdClose className="mr-2" />
              Reject
            </button>
          </div>
        )}
      </div>
      <Header title="Enrollment Details" />

      {/* Submission Details */}
      <div className="mb-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Submission Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusStyles[status]}`}>
              {statusText}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-500">Class</p>
            <p className="font-medium text-blue-600">
              {getClassName(enrollment?.class) || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Submitted On</p>
            <p className="font-medium">{new Date(enrollment.createdAt).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Last Updated</p>
            <p className="font-medium">{new Date(enrollment.updatedAt).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Dynamic Sections */}
      <div className="space-y-8">
        {groupFieldsBySection().map((section, sectionIndex) => (
          <div key={sectionIndex} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800">{section.title}</h2>
              {section.description && (
                <p className="text-sm text-gray-600 mt-1">{section.description}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {section.fields.map(field => (
                <div key={field.name} className="space-y-1">
                  {renderField(field)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* File Preview Modal */}
      {showFileModal && selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl max-h-[90vh] w-full overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 text-blue-600">
                  {getFileIcon(selectedFile.url)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedFile.name}</h3>
                  <p className="text-sm text-gray-500">{getFileType(selectedFile.url).toUpperCase()} file</p>
                </div>
              </div>
              <button
                onClick={() => setShowFileModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
              >
                <MdClose size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
              {canPreview(selectedFile.url) ? (
                <div className="text-center">
                  <img
                    src={selectedFile.url}
                    alt={selectedFile.name}
                    className="max-w-full h-auto mx-auto rounded-lg shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden flex-col items-center justify-center py-12">
                    <div className="mb-4">
                      <svg className="w-16 h-16 text-red-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-lg font-medium text-gray-700 mb-2">Failed to load image</p>
                    <p className="text-sm text-gray-500 mb-4">The image could not be displayed.</p>
                    <a
                      href={selectedFile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <MdDownload className="mr-2" size={16} />
                      Download File
                    </a>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mb-6">
                    {isDocument(selectedFile.url) ? (
                      <svg className="w-20 h-20 text-purple-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-20 h-20 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  
                  <h4 className="text-xl font-semibold text-gray-800 mb-2">
                    {isDocument(selectedFile.url) ? 'Document Preview' : 'File Preview'}
                  </h4>
                  <p className="text-gray-600 mb-6">
                    {isDocument(selectedFile.url) 
                      ? 'This document cannot be previewed in the browser. You can view it in a new tab or download it.'
                      : 'This file type cannot be previewed in the browser.'
                    }
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {isDocument(selectedFile.url) && (
                      <a
                        href={selectedFile.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                      >
                        <svg className="mr-2 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        View in New Tab
                      </a>
                    )}
                    <a
                      href={selectedFile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      <MdDownload className="mr-2" size={16} />
                      Download File
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrollmentDetails;